import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Plus, AlertCircle, LogIn } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Skeleton } from '../ui/Skeleton';
import AskQuestionForm from './AskQuestionForm';
import QuestionsList from './QuestionsList';
import { useAuth } from '../../hooks/useAuth';
import questionService from '../../services/questionService';
import toast from 'react-hot-toast';

const QASection = ({ project, onAuthRequired }) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 10;

  // Check if user is the project owner
  const isProjectOwner = isAuthenticated && user?.id === project?.owner_id;

  // Fetch questions
  const {
    data: questionsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['project-questions', project?.slug, page],
    queryFn: () => questionService.getProjectQuestions(project.slug, {
      limit,
      offset: page * limit
    }),
    enabled: !!project?.slug,
    keepPreviousData: true
  });

  // Fetch question stats
  const { data: statsData } = useQuery({
    queryKey: ['question-stats', project?.slug],
    queryFn: () => questionService.getQuestionStats(project.slug),
    enabled: !!project?.slug
  });

  // Create question mutation with optimistic updates
  const createQuestionMutation = useMutation({
    mutationFn: ({ question }) => questionService.createQuestion(project.slug, question),
    onMutate: async ({ question }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['project-questions', project.slug]);
      await queryClient.cancelQueries(['question-stats', project.slug]);

      // Snapshot the previous values
      const previousQuestions = queryClient.getQueryData(['project-questions', project.slug, 0]);
      const previousStats = queryClient.getQueryData(['question-stats', project.slug]);

      // Create optimistic question
      const optimisticQuestion = {
        id: `temp-${Date.now()}`,
        question: question,
        is_answered: false,
        is_pinned: false,
        created_at: new Date().toISOString(),
        user: {
          id: user.id,
          pi_username: user.pi_username,
          full_name: user.full_name,
          profile_image: user.profile_image,
          badge_level: user.badge_level,
          total_donated: user.total_donated
        },
        answer: null
      };

      // Optimistically update questions
      if (previousQuestions) {
        queryClient.setQueryData(['project-questions', project.slug, 0], {
          ...previousQuestions,
          data: {
            ...previousQuestions.data,
            questions: [optimisticQuestion, ...previousQuestions.data.questions],
            totalCount: previousQuestions.data.totalCount + 1
          }
        });
      }

      // Optimistically update stats
      if (previousStats) {
        queryClient.setQueryData(['question-stats', project.slug], {
          ...previousStats,
          data: {
            ...previousStats.data,
            totalQuestions: previousStats.data.totalQuestions + 1,
            unansweredQuestions: previousStats.data.unansweredQuestions + 1
          }
        });
      }

      return { previousQuestions, previousStats };
    },
    onSuccess: (data) => {
      toast.success('Question submitted successfully!');
      setShowQuestionForm(false);
      // Reset pagination to show new question at top
      setPage(0);
      // Invalidate and refetch to get real data
      queryClient.invalidateQueries(['project-questions', project.slug]);
      queryClient.invalidateQueries(['question-stats', project.slug]);
    },
    onError: (error, variables, context) => {
      toast.error(error.message || 'Failed to submit question');
      // Rollback optimistic updates
      if (context?.previousQuestions) {
        queryClient.setQueryData(['project-questions', project.slug, 0], context.previousQuestions);
      }
      if (context?.previousStats) {
        queryClient.setQueryData(['question-stats', project.slug], context.previousStats);
      }
    }
  });

  const handleQuestionSubmit = (questionText) => {
    createQuestionMutation.mutate({ question: questionText });
  };

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const questions = questionsData?.data?.questions || [];
  const hasMore = questionsData?.data?.hasMore || false;
  const totalQuestions = statsData?.data?.totalQuestions || 0;
  const answeredQuestions = statsData?.data?.answeredQuestions || 0;
  const unansweredQuestions = statsData?.data?.unansweredQuestions || 0;

  if (isLoading && page === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Skeleton className="w-8 h-8 rounded-lg" />
            <Skeleton className="h-6 w-48" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-16 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-neutral-400" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Unable to load questions
          </h3>
          <p className="text-neutral-600 mb-4">
            {error.message || 'Something went wrong while loading the Q&A section.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header with Stats */}
      <Card className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300">
        <CardHeader className="pb-4">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-primary-teal" />
              </div>
              <span className="text-lg sm:text-xl">Ask about this project</span>
            </div>
            {(!isAuthenticated || !isProjectOwner) && (
              <Button
                onClick={() => {
                  if (!isAuthenticated) {
                    onAuthRequired && onAuthRequired();
                    return;
                  }
                  setShowQuestionForm(!showQuestionForm);
                }}
                size="sm"
                className="flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Plus className="w-4 h-4" />
                <span className="sm:inline">{showQuestionForm ? 'Cancel' : 'Ask Question'}</span>
              </Button>
            )}
          </CardTitle>

          {/* Stats */}
          {totalQuestions > 0 && (
            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-neutral-600 mt-3">
              <span className="bg-neutral-100 px-2 py-1 rounded-full">{totalQuestions} total questions</span>
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">{answeredQuestions} answered</span>
              {unansweredQuestions > 0 && (
                <span className="bg-red-100 text-secondary-red px-2 py-1 rounded-full">{unansweredQuestions} awaiting response</span>
              )}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {/* Question Form */}
          <AnimatePresence>
            {showQuestionForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-6"
              >
                <AskQuestionForm
                  onSubmit={handleQuestionSubmit}
                  onCancel={() => setShowQuestionForm(false)}
                  isSubmitting={createQuestionMutation.isLoading}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Questions List */}
          {questions.length > 0 ? (
            <div className="space-y-4">
              <QuestionsList
                questions={questions}
                projectSlug={project.slug}
                isProjectOwner={isProjectOwner}
                currentUser={user}
              />

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    onClick={handleLoadMore}
                    variant="outline"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Load More Questions'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No questions yet
              </h3>
              <p className="text-neutral-600 mb-4">
                Be the first to ask the project owner a question!
              </p>
              {isAuthenticated && !isProjectOwner && !showQuestionForm && (
                <Button
                  onClick={() => setShowQuestionForm(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Ask the First Question
                </Button>
              )}
              {!isAuthenticated && (
                <div className="space-y-4">
                  <p className="text-sm text-neutral-500">
                    Sign in to ask the project owner a question!
                  </p>
                  <Button
                    onClick={() => onAuthRequired && onAuthRequired()}
                    variant="outline"
                    className="flex items-center gap-2 mx-auto"
                  >
                    <LogIn className="w-4 h-4" />
                    Sign In to Ask
                  </Button>
                </div>
              )}
              {isProjectOwner && (
                <p className="text-sm text-neutral-500">
                  Questions from supporters will appear here
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default QASection;
